import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  SearchIcon, 
  ClockIcon, 
  AlertTriangleIcon,
  CheckCircleIcon,
  TrendingUpIcon,
  SettingsIcon
} from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { getApiEndpoint } from "@/lib/api";
import { getToken, getUser } from "@/lib/auth-storage";
import { useToast } from "@/hooks/use-toast";
import { useLanguage } from "@/contexts/LanguageContext";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

interface Service {
  id: string;
  name: string;
  description: string;
  category: string;
  type: 'incident' | 'request' | 'problem' | 'change';
  sla?: string | null;
  estimated_time?: string | null;
  icon: any;
  source?: "default" | "db";
}

const ServiceCatalog = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useLanguage();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [catalogServices, setCatalogServices] = useState<Service[]>([]);
  const [loadingCatalog, setLoadingCatalog] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [companies, setCompanies] = useState<Array<{ id: number; name: string; domain: string }>>([]);
  const [newService, setNewService] = useState({
    name: "",
    description: "",
    category: "",
    type: "incident" as Service["type"],
    sla: "",
    estimated_time: "",
    company_id: "none",
  });

  const defaultServices: Service[] = useMemo(() => [
    {
      id: '1',
      name: t.serviceCatalog.default1Name,
      description: t.serviceCatalog.default1Desc,
      category: t.serviceCatalog.default1Category,
      type: 'incident',
      sla: '4h - 48h',
      estimated_time: '2-4h',
      icon: SettingsIcon,
      source: "default"
    },
    {
      id: '2',
      name: t.serviceCatalog.default2Name,
      description: t.serviceCatalog.default2Desc,
      category: t.serviceCatalog.default2Category,
      type: 'incident',
      sla: '24h - 48h',
      estimated_time: '1-3d',
      icon: AlertTriangleIcon,
      source: "default"
    },
    {
      id: '3',
      name: t.serviceCatalog.default3Name,
      description: t.serviceCatalog.default3Desc,
      category: t.serviceCatalog.default3Category,
      type: 'incident',
      sla: '8h - 24h',
      estimated_time: '1-2h',
      icon: CheckCircleIcon,
      source: "default"
    },
    {
      id: '4',
      name: t.serviceCatalog.default4Name,
      description: t.serviceCatalog.default4Desc,
      category: t.serviceCatalog.default4Category,
      type: 'request',
      sla: '24h - 48h',
      estimated_time: '1-2d',
      icon: CheckCircleIcon,
      source: "default"
    },
    {
      id: '5',
      name: t.serviceCatalog.default5Name,
      description: t.serviceCatalog.default5Desc,
      category: t.serviceCatalog.default5Category,
      type: 'problem',
      sla: '24h - 48h',
      estimated_time: '2-5d',
      icon: TrendingUpIcon,
      source: "default"
    },
    {
      id: '6',
      name: t.serviceCatalog.default6Name,
      description: t.serviceCatalog.default6Desc,
      category: t.serviceCatalog.default6Category,
      type: 'change',
      sla: '48h - 72h',
      estimated_time: '3-7d',
      icon: SettingsIcon,
      source: "default"
    }
  ], [t]);

  useEffect(() => {
    const userStr = getUser();
    if (userStr) {
      try {
        setCurrentUser(JSON.parse(userStr));
      } catch {
        setCurrentUser(null);
      }
    }
  }, []);

  const iconForType = (type: Service["type"]) => {
    switch (type) {
      case "incident": return AlertTriangleIcon;
      case "request": return CheckCircleIcon;
      case "problem": return TrendingUpIcon;
      case "change": return SettingsIcon;
      default: return SettingsIcon;
    }
  };

  const fetchCatalog = async () => {
    try {
      setLoadingCatalog(true);
      const token = getToken();
      const res = await fetch(getApiEndpoint("/api/catalog"), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(t.serviceCatalog.errorLoad);
      const data = await res.json();
      const rows = Array.isArray(data?.services) ? data.services : [];
      const mapped: Service[] = rows.map((r: any) => ({
        id: `db-${r.id}`,
        name: r.name,
        description: r.description,
        category: r.category,
        type: r.type,
        sla: r.sla || "",
        estimated_time: r.estimated_time || "",
        icon: iconForType(r.type),
        source: "db",
      }));
      setCatalogServices(mapped);
    } catch (e: any) {
      toast({
        title: t.serviceCatalog.error,
        description: e?.message || t.serviceCatalog.errorLoad,
        variant: "destructive",
      });
    } finally {
      setLoadingCatalog(false);
    }
  };

  const fetchCompanies = async () => {
    try {
      const token = getToken();
      const res = await fetch(getApiEndpoint("/api/companies"), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const data = await res.json();
      setCompanies(Array.isArray(data?.companies) ? data.companies : []);
    } catch {
      // silencioso
    }
  };

  useEffect(() => {
    fetchCatalog();
  }, []);

  useEffect(() => {
    if (currentUser?.role === "admin") {
      fetchCompanies();
    }
  }, [currentUser?.role]);

  const allServices: Service[] = [...defaultServices, ...catalogServices];
  const categories = Array.from(new Set(allServices.map(s => s.category)));

  const filteredServices = allServices.filter(service => {
    const matchesSearch = service.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         service.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === "all" || service.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'incident': return t.serviceCatalog.incident;
      case 'request': return t.serviceCatalog.request;
      case 'problem': return t.serviceCatalog.problem;
      case 'change': return t.serviceCatalog.change;
      default: return type;
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'incident': return 'bg-red-100 text-red-800';
      case 'request': return 'bg-blue-100 text-blue-800';
      case 'problem': return 'bg-purple-100 text-purple-800';
      case 'change': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <Header />
      
      <main className="container mx-auto px-4 py-8 max-w-7xl">
        <div className="mb-8 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">{t.serviceCatalog.title}</h1>
            <p className="text-gray-600 dark:text-gray-400">
              {t.serviceCatalog.subtitleAlt}
            </p>
          </div>
          {(currentUser?.role === "internal" || currentUser?.role === "admin") && (
            <Button onClick={() => setAddDialogOpen(true)}>
              {t.serviceCatalog.addService}
            </Button>
          )}
        </div>

        {/* Busca e Filtros */}
        <div className="mb-6 space-y-4">
          <div className="relative">
            <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder={t.serviceCatalog.searchPlaceholder}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          
          <Tabs value={selectedCategory} onValueChange={setSelectedCategory}>
            <TabsList>
              <TabsTrigger value="all">{t.serviceCatalog.all}</TabsTrigger>
              {categories.map(category => (
                <TabsTrigger key={category} value={category}>
                  {category}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </div>

        {/* Lista de Serviços */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredServices.map((service) => {
            const IconComponent = service.icon;
            return (
              <Card key={service.id} className="flex h-full flex-col dark:bg-gray-800 dark:border-gray-700">
                <CardHeader>
                  <div className="flex items-start justify-between mb-2">
                    <IconComponent className="h-8 w-8 text-blue-600 dark:text-blue-400" />
                    <div className="flex items-center gap-2">
                      {(service.source === "db") && (currentUser?.role === "internal" || currentUser?.role === "admin") && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={async () => {
                            try {
                              const ok = window.confirm(t.serviceCatalog.removeConfirm);
                              if (!ok) return;
                              const token = getToken();
                              const numericId = service.id.replace("db-", "");
                              const res = await fetch(getApiEndpoint(`/api/catalog/${numericId}`), {
                                method: "DELETE",
                                headers: { Authorization: `Bearer ${token}` },
                              });
                              const data = await res.json().catch(() => ({}));
                              if (!res.ok) throw new Error(data?.error || t.serviceCatalog.errorRemove);
                              toast({ title: t.serviceCatalog.removed, description: t.serviceCatalog.removedDesc });
                              await fetchCatalog();
                            } catch (e: any) {
                              toast({
                                title: t.serviceCatalog.error,
                                description: e?.message || t.serviceCatalog.errorRemove,
                                variant: "destructive",
                              });
                            }
                          }}
                          title={t.serviceCatalog.removeTitle}
                        >
                          {t.serviceCatalog.deleteBtn}
                        </Button>
                      )}
                      <Badge className={getTypeColor(service.type)}>
                        {getTypeLabel(service.type)}
                      </Badge>
                    </div>
                  </div>
                  <CardTitle className="text-xl dark:text-white">{service.name}</CardTitle>
                  <CardDescription className="dark:text-gray-400">{service.description}</CardDescription>
                </CardHeader>
                <CardContent className="flex flex-1 flex-col">
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600 dark:text-gray-400">{t.serviceCatalog.category}</span>
                      <span className="font-medium dark:text-gray-200">{service.category}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600 dark:text-gray-400 flex items-center gap-1">
                        <ClockIcon className="h-4 w-4" />
                        {t.serviceCatalog.sla}
                      </span>
                      <span className="font-medium dark:text-gray-200">{service.sla || "-"}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600 dark:text-gray-400">{t.serviceCatalog.estimatedTime}</span>
                      <span className="font-medium dark:text-gray-200">{service.estimated_time || "-"}</span>
                    </div>
                  </div>
                  <div className="mt-auto pt-4">
                    <Button 
                      className="w-full" 
                      onClick={() => navigate('/novo-chamado', {
                        state: {
                          preselectedService: {
                            name: service.name,
                            description: service.description,
                            category: service.category,
                            type: service.type
                          }
                        }
                      })}
                    >
                      {t.serviceCatalog.requestService}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {filteredServices.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-600 dark:text-gray-400">
              {loadingCatalog ? t.serviceCatalog.loading : t.serviceCatalog.noServicesFilter}
            </p>
          </div>
        )}
      </main>

      <Footer />

      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>{t.serviceCatalog.addServiceTitle}</DialogTitle>
            <DialogDescription>
              {t.serviceCatalog.addServiceDesc}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>{t.serviceCatalog.name}</Label>
              <Input
                value={newService.name}
                onChange={(e) => setNewService((p) => ({ ...p, name: e.target.value }))}
                placeholder={t.serviceCatalog.namePlaceholder}
              />
            </div>

            <div className="space-y-2">
              <Label>{t.serviceCatalog.description}</Label>
              <Textarea
                value={newService.description}
                onChange={(e) => setNewService((p) => ({ ...p, description: e.target.value }))}
                placeholder={t.serviceCatalog.descriptionPlaceholder}
                rows={3}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t.serviceCatalog.categoryLabel}</Label>
                <Input
                  value={newService.category}
                  onChange={(e) => setNewService((p) => ({ ...p, category: e.target.value }))}
                  placeholder={t.serviceCatalog.categoryPlaceholder}
                />
              </div>
              <div className="space-y-2">
                <Label>{t.serviceCatalog.type}</Label>
                <Select
                  value={newService.type}
                  onValueChange={(v) => setNewService((p) => ({ ...p, type: v as Service["type"] }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t.serviceCatalog.select} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="incident">{t.serviceCatalog.incident}</SelectItem>
                    <SelectItem value="request">{t.serviceCatalog.request}</SelectItem>
                    <SelectItem value="problem">{t.serviceCatalog.problem}</SelectItem>
                    <SelectItem value="change">{t.serviceCatalog.change}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t.serviceCatalog.slaOptional}</Label>
                <Input
                  value={newService.sla}
                  onChange={(e) => setNewService((p) => ({ ...p, sla: e.target.value }))}
                  placeholder={t.serviceCatalog.slaPlaceholder}
                />
              </div>
              <div className="space-y-2">
                <Label>{t.serviceCatalog.estimatedTimeOptional}</Label>
                <Input
                  value={newService.estimated_time}
                  onChange={(e) => setNewService((p) => ({ ...p, estimated_time: e.target.value }))}
                  placeholder={t.serviceCatalog.estimatedTimePlaceholder}
                />
              </div>
            </div>

            {currentUser?.role === "admin" && (
              <div className="space-y-2">
                <Label>{t.serviceCatalog.company}</Label>
                <Select
                  value={newService.company_id}
                  onValueChange={(v) => setNewService((p) => ({ ...p, company_id: v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t.serviceCatalog.selectCompany} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">{t.serviceCatalog.selectPlaceholder}</SelectItem>
                    {companies.map((c) => (
                      <SelectItem key={c.id} value={String(c.id)}>
                        {c.name} ({c.domain})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500">
                  {t.serviceCatalog.companyRequiredHint}
                </p>
              </div>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setAddDialogOpen(false)}>
              {t.serviceCatalog.cancel}
            </Button>
            <Button
              onClick={async () => {
                try {
                  if (!newService.name.trim() || !newService.description.trim() || !newService.category.trim() || !newService.type) {
                    toast({
                      title: t.serviceCatalog.requiredFields,
                      description: t.serviceCatalog.requiredFieldsDesc,
                      variant: "destructive",
                    });
                    return;
                  }
                  if (currentUser?.role === "admin" && (!newService.company_id || newService.company_id === "none")) {
                    toast({
                      title: t.serviceCatalog.companyRequired,
                      description: t.serviceCatalog.companyRequiredDesc,
                      variant: "destructive",
                    });
                    return;
                  }

                  const token = getToken();
                  const payload: any = {
                    name: newService.name.trim(),
                    description: newService.description.trim(),
                    category: newService.category.trim(),
                    type: newService.type,
                    sla: newService.sla?.trim() || null,
                    estimated_time: newService.estimated_time?.trim() || null,
                  };
                  if (currentUser?.role === "admin") {
                    payload.company_id = parseInt(newService.company_id);
                  }

                  const res = await fetch(getApiEndpoint("/api/catalog"), {
                    method: "POST",
                    headers: {
                      Authorization: `Bearer ${token}`,
                      "Content-Type": "application/json",
                    },
                    body: JSON.stringify(payload),
                  });
                  const data = await res.json().catch(() => ({}));
                  if (!res.ok) throw new Error(data?.error || t.serviceCatalog.errorAdd);

                  toast({ title: t.serviceCatalog.serviceAdded, description: t.serviceCatalog.serviceAddedDesc });
                  setAddDialogOpen(false);
                  setNewService({
                    name: "",
                    description: "",
                    category: "",
                    type: "incident",
                    sla: "",
                    estimated_time: "",
                    company_id: "none",
                  });
                  await fetchCatalog();
                } catch (e: any) {
                  toast({
                    title: t.serviceCatalog.error,
                    description: e?.message || t.serviceCatalog.errorAdd,
                    variant: "destructive",
                  });
                }
              }}
            >
              {t.serviceCatalog.save}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ServiceCatalog;

